import { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import HeroCarousel from "./components/HeroCarousel";
import WhyRegisterSection from "./components/WhyRegisterSection";
import PackagesSection from "./components/PackagesSection";
import Footer from "./components/Footer";
import BottomBar from "./components/BottomBar";
import RegistrationPage from "./components/RegistrationPage";
import KycPage from "./components/kyc/KycPage";
import { Toaster } from "./components/ui/toaster";

const Home = () => {
  // Initialise activeTab from URL query param so deep links like /?type=business work
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('type') === 'business' ? 'business' : 'home';
    }
    return 'home';
  });

  // On initial mount, if URL has ?type=... scroll to Packages section automatically
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('type')) {
      // Wait for layout to settle before scrolling
      setTimeout(() => {
        const packagesSection = document.getElementById('package');
        if (packagesSection) {
          packagesSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 150);
    }
  }, []);

  const handleGetItNow = (pkg = null) => {
    // Open the standalone registration page in a new tab with selection pre-filled via query params
    const params = new URLSearchParams();
    if (pkg?.speed) params.set('speed', pkg.speed);
    if (pkg?.selectedOptional) params.set('addon', pkg.selectedOptional);
    if (pkg?.selectedDeviceAddon) params.set('promo', pkg.selectedDeviceAddon);
    if (activeTab === 'business') params.set('type', 'business');
    const qs = params.toString();
    window.open(qs ? `/registration_form?${qs}` : '/registration_form', '_blank', 'noopener,noreferrer');
  };

  const handleRegisterClick = () => {
    window.open('/registration_form', '_blank', 'noopener,noreferrer');
  };

  const scrollToPackages = () => {
    const packagesSection = document.getElementById('package');
    if (packagesSection) {
      packagesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavTabSelect = (tab) => {
    setActiveTab(tab);

    // Update URL query param so the selection is shareable / bookmarkable
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (tab === 'business') {
        url.searchParams.set('type', 'business');
      } else {
        url.searchParams.delete('type');
      }
      window.history.replaceState({}, '', url.toString());
    }

    setTimeout(() => {
      const packagesSection = document.getElementById('package');
      if (packagesSection) {
        packagesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar onGetItNowClick={scrollToPackages} onSelectTab={handleNavTabSelect} />
      <HeroCarousel />
      <WhyRegisterSection />
      <PackagesSection onGetItNow={handleGetItNow} activeTab={activeTab} setActiveTab={setActiveTab} />
      <Footer onRegisterClick={handleRegisterClick} />

      {/* Fixed Bottom Bar */}
      <BottomBar onRegisterClick={handleRegisterClick} />

      <Toaster />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/registration_form" element={<RegistrationPage />} />
          <Route path="/kyc" element={<KycPage />} />
          {/* Catch-all fallback for bot probes and mistyped URLs.
              Any unknown path is redirected:
                - If it contains the word "business" (case-insensitive) -> Business tab
                - Otherwise -> Home page
              `replace` avoids polluting browser history. This is a safety net
              in case the Cloudflare Redirect Rules at the edge miss anything. */}
          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

// Catch-all redirect component for unknown paths
const NotFoundRedirect = () => {
  const location = useLocation();
  const hasBusiness = location.pathname.toLowerCase().includes('business');
  const target = hasBusiness ? '/?type=business' : '/';
  return <Navigate to={target} replace />;
};

export default App;
